import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Checkbox } from "primereact/checkbox";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { Skeleton } from "primereact/skeleton";
import React, { useEffect, useState } from "react";
import { getItem } from "../../../../../utils/localStorage";
import { makeRequest2 } from "../../../../../utils/makeRequest";
import { toastify } from "../../../../../utils/toast";
import { GET_CURRENCIES_URL, get_lease_penalties, get_lease_reminders, getTaxRates, getDigitaxConfigURL } from "../../../../../utils/urls";

const FinancialTermsTab = ({
  formData,
  updateFormData,
  onNext,
  onPrevious,
  validation
}) => {
  const [penalties, setPenalties] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [fetchedTaxRates, setFetchedTaxRates] = useState([]);

  const [digitaxProfiles, setDigitaxProfiles] = useState([]);
  const [digitaxSelection, setDigitaxSelection] = useState("");

  const fetchDigitaxProfiles = async () => {
    try {
      const facilityId = await getItem("selectedFacilityId");
      const response = await makeRequest2(
        `${getDigitaxConfigURL}/${facilityId}`,
        "GET"
      );

      console.log("Digitax config response:", response);

      if (response.success) {
        setDigitaxProfiles(
          response.data?.digitax?.profiles || []
        );
      }
    } catch (error) {
      console.error(
        "Failed to fetch DigiTax profiles:",
        error
      );
    }
  };

  useEffect(() => {
    fetchDropdownData();
    fetchTaxRates();
    fetchDigitaxProfiles();
  }, []);

  const fetchTaxRates = async () => {
    try {
      const facilityId = await getItem("selectedFacilityId");
      const response = await makeRequest2(
        `${getTaxRates}/${facilityId}`,
        "GET"
      );
      console.log("Tax rates response:", response);

      if (response.success && response.data) {
        const taxData = response.data.data || response.data;
        const taxRatesArray = Array.isArray(taxData) ? taxData : [taxData];
        setFetchedTaxRates(taxRatesArray);
      }
    } catch (error) {
      console.error("Error fetching tax rates:", error);
      toastify("Error fetching tax rates", "error");
    }
  };

  const fetchDropdownData = async () => {
    try {
      const facilityId = await getItem("selectedFacilityId");

      if (!facilityId) {
        toastify("No facility selected. Please select a facility first.", "error");
        return;
      }

      const [penaltiesRes, remindersRes, currenciesRes] = await Promise.all([
        makeRequest2(`${get_lease_penalties}/${facilityId}`, "GET"),
        makeRequest2(`${get_lease_reminders}/${facilityId}`, "GET"),
        makeRequest2(`${GET_CURRENCIES_URL}/${facilityId}`, "GET")
      ]);

      console.log("Penalties response:", penaltiesRes);
      console.log("Reminders response:", remindersRes);

      // Handle penalties response with multiple possible structures
      if (penaltiesRes && penaltiesRes.success) {
        let penaltiesData = [];

        if (Array.isArray(penaltiesRes.data)) {
          penaltiesData = penaltiesRes.data;
        } else if (penaltiesRes.data && penaltiesRes.data.penalties) {
          penaltiesData = penaltiesRes.data.penalties;
        } else if (penaltiesRes.data && penaltiesRes.data.data) {
          penaltiesData = penaltiesRes.data.data;
        }

        const activePenalties = penaltiesData.filter(p => p.isActive !== false) || [];
        setPenalties(activePenalties);
      } else {
        console.error("Failed to fetch penalties:", penaltiesRes);
        toastify("Failed to load penalties", "error");
      }

      // Handle reminders response with multiple possible structures
      if (remindersRes && remindersRes.success) {
        let remindersData = [];

        if (Array.isArray(remindersRes.data)) {
          remindersData = remindersRes.data;
        } else if (remindersRes.data && remindersRes.data.reminders) {
          remindersData = remindersRes.data.reminders;
        } else if (remindersRes.data && remindersRes.data.data) {
          remindersData = remindersRes.data.data;
        }

        const activeReminders = remindersData.filter(r => r.isActive !== false) || [];
        setReminders(activeReminders);
        console.log("Reminders loaded:", activeReminders.length, activeReminders);
      } else {
        console.error("Failed to fetch reminders:", remindersRes);
        toastify("Failed to load reminders", "error");
      }

      // Handle Currencies Response
      if (currenciesRes && currenciesRes.success) {
        let currencyData = [];

        if (Array.isArray(currenciesRes.data)) {
          currencyData = currenciesRes.data;
        } else if (currenciesRes.data && currenciesRes.data.currencies) {
          currencyData = currenciesRes.data.currencies;
        } else if (currenciesRes.data && currenciesRes.data.data) {
          currencyData = currenciesRes.data.data;
        }

        setCurrencies(currencyData);
        updateFormData.updateBasicInfo({ availableCurrencies: currencyData });
      } else {
        toastify("Failed to load currencies", "error");
      }
    } catch (error) {
      console.error("Error fetching dropdown data:", error);
      toastify("Failed to load penalties and reminders", "error");
    }
  };

  // Guard against undefined formData
  if (!formData || !formData.financialTerms || !formData.basicInfo) {
    return (
      <div className="surface-card p-6 border-round-lg shadow-2">
        <div className="flex align-items-center mb-4">
          <i className="ti ti-currency-dollar text-2xl text-primary mr-3"></i>
          <h3 className="text-xl font-semibold text-900 m-0">Financial Terms</h3>
        </div>
        <div className="grid">
          <div className="col-12 md:col-6 mb-4">
            <div className="field">
              <label className="block text-900 font-medium mb-2">Monthly Rent</label>
              <Skeleton width="100%" height="3rem"></Skeleton>
            </div>
          </div>
          <div className="col-12 md:col-6 mb-4">
            <div className="field">
              <label className="block text-900 font-medium mb-2">Payment Due Date</label>
              <Skeleton width="100%" height="3rem"></Skeleton>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const { financialTerms, basicInfo } = formData;

  const handleNext = () => {
    const validationResult = validation.validateFinancialTerms();

    if (!validationResult.isValid) {
      validationResult.errors.forEach(error => {
        toastify(error, "error");
      });
      return;
    }

    onNext();
  };

  const calculateTotalDeposit = () => {
    const monthlyRent = parseFloat(financialTerms.monthlyRent) || 0;
    const depositMonths = parseInt(financialTerms.depositMonths) || 1;
    const manualDeposit = parseFloat(financialTerms.securityDeposit) || 0;

    return financialTerms.manualDepositEntry ? manualDeposit : monthlyRent * depositMonths;
  };

  const formatCurrency = (amount) => {
    const currencySymbol = (basicInfo.selectedCurrency && basicInfo.selectedCurrency.currencySymbol) || '';
    return `${currencySymbol} ${amount.toLocaleString()}`;
  };

  return (
    <React.Fragment>
      <div className="row">
        {/* Currency Selection */}
        <div className="col-md-6 mb-3">
          <div className="form-group">
            <label htmlFor="currency">
              Currency <span className="text-danger">*</span>
            </label>
            <Dropdown
              id="currency"
              value={basicInfo.selectedCurrency && basicInfo.selectedCurrency.value}
              options={currencies.map(curr => ({
                label: `${curr.currencyName} (${curr.currencyShortCode})`,
                value: curr._id,
                currencyShortCode: curr.currencyShortCode,
                currencySymbol: curr.currencySymbol,
                currencyName: curr.currencyName
              }))}
              onChange={(e) => {
                const selectedCurrency = currencies.find(c => c._id === e.value);
                updateFormData.updateBasicInfo({
                  selectedCurrency: {
                    value: e.value,
                    label: selectedCurrency ? `${selectedCurrency.currencyName} (${selectedCurrency.currencyShortCode})` : "",
                    currencyShortCode: selectedCurrency && selectedCurrency.currencyShortCode,
                    currencySymbol: selectedCurrency && selectedCurrency.currencySymbol,
                    currencyName: selectedCurrency && selectedCurrency.currencyName
                  }
                });
              }}
              placeholder="Select currency"
              className="w-100"
            />
          </div>
        </div>

        {/* Monthly Rent */}
        <div className="col-md-6 mb-3">
          <div className="form-group">
            <label htmlFor="monthlyRent">
              Monthly Rent <span className="text-danger">*</span>
            </label>
            <InputNumber
              id="monthlyRent"
              value={parseFloat(financialTerms.monthlyRent) || null}
              onValueChange={(e) => updateFormData.updateFinancialTerms({ monthlyRent: (e.value && e.value.toString()) || "" })}
              placeholder="Enter monthly rent amount"
              className="w-100"
              min={0}
              currency={(basicInfo.selectedCurrency && basicInfo.selectedCurrency.currencyShortCode) || "USD"}
              mode="currency"
            />
          </div>
        </div>

        {/* Payment Due Date */}
        <div className="col-md-6 mb-3">
          <div className="form-group">
            <label htmlFor="paymentDueDate">
              Payment Due Date <span className="text-danger">*</span>
            </label>
            <InputNumber
              id="paymentDueDate"
              value={parseInt(financialTerms.paymentDueDate) || null}
              onValueChange={(e) => updateFormData.updateFinancialTerms({ paymentDueDate: (e.value && e.value.toString()) || "" })}
              placeholder="Day of month (1-31)"
              className="w-100"
              min={1}
              max={31}
              suffix=" of each month"
            />
          </div>
        </div>

        {/* Security Deposit Section */}
        <div className="col-12 mb-3">
          <div className="card">
            <div className="card-body">
              <h6 className="card-title text-primary">Security Deposit</h6>

              <div className="row">
                {/* Manual Deposit Entry Toggle */}
                <div className="col-12 mb-3">
                  <div className="form-check">
                    <Checkbox
                      inputId="manualDepositEntry"
                      checked={financialTerms.manualDepositEntry}
                      onChange={(e) => updateFormData.updateFinancialTerms({ manualDepositEntry: e.checked })}
                      className="form-check-input"
                    />
                    <label htmlFor="manualDepositEntry" className="form-check-label">
                      Enter custom deposit amount
                    </label>
                  </div>
                  <small className="text-muted">
                    {financialTerms.manualDepositEntry
                      ? "Custom deposit amount"
                      : `${financialTerms.depositMonths || 1} months × ${formatCurrency(parseFloat(financialTerms.monthlyRent) || 0)}`
                    }
                  </small>
                </div>

                {!financialTerms.manualDepositEntry ? (
                  // Automatic Calculation
                  <div className="col-md-6 mb-3">
                    <div className="form-group">
                      <label>Deposit Months</label>
                      <InputNumber
                        value={parseInt(financialTerms.depositMonths) || 1}
                        onValueChange={(e) => updateFormData.updateFinancialTerms({ depositMonths: e.value != null ? e.value : 1 })}
                        placeholder="Number of months"
                        className="w-100"
                        min={0}
                        max={12}
                        suffix=" months"
                      />
                      <small className="text-muted">
                        Number of months' rent for security deposit
                      </small>
                    </div>
                  </div>
                ) : (
                  // Manual Entry
                  <div className="col-md-6 mb-3">
                    <div className="form-group">
                      <label>
                        Security Deposit Amount <span className="text-danger">*</span>
                      </label>
                      <InputNumber
                        value={parseFloat(financialTerms.securityDeposit) || null}
                        onValueChange={(e) => updateFormData.updateFinancialTerms({ securityDeposit: (e.value != null ? e.value.toString() : "") })}
                        placeholder="Enter deposit amount"
                        className="w-100"
                        min={0}
                        currency={(basicInfo.selectedCurrency && basicInfo.selectedCurrency.currencyShortCode) || "USD"}
                        mode="currency"
                      />
                    </div>
                  </div>
                )}

                {/* Calculated Deposit Display */}
                <div className="col-md-6 mb-3">
                  <div className="form-group">
                    <label>Total Security Deposit</label>
                    <div className="bg-light p-2 border rounded">
                      <strong>{formatCurrency(calculateTotalDeposit())}</strong>
                    </div>
                    <small className="text-muted">
                      {financialTerms.manualDepositEntry
                        ? "Custom deposit amount"
                        : `${financialTerms.depositMonths} months × ${formatCurrency(parseFloat(financialTerms.monthlyRent) || 0)}`
                      }
                    </small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Balance Brought Forward */}
        <div className="col-md-6 mb-3">
          <div className="form-group">
            <label>Balance Brought Forward</label>
            <InputNumber
              value={parseFloat(financialTerms.balanceBroughtForward) || null}
              onValueChange={(e) => updateFormData.updateFinancialTerms({ balanceBroughtForward: (e.value && e.value.toString()) || "0" })}
              placeholder="Outstanding balance (if any)"
              className="w-100"
              currency={(basicInfo.selectedCurrency && basicInfo.selectedCurrency.currencyShortCode) || "USD"}
              mode="currency"
            />
            <small className="text-muted">
              Any outstanding balance from previous agreements or charges
            </small>
          </div>
        </div>



        <div className="col-md-6 mb-3">
          <div className="form-group">
            <label>Applicable Taxes</label>

            {fetchedTaxRates.length > 0 ? (
              fetchedTaxRates.map((tax) => (
                <div className="form-check" key={tax._id}>
                  <Checkbox
                    inputId={`tax-${tax._id}`}
                    checked={financialTerms.enabledTaxes?.includes(tax._id)}
                    onChange={(e) => {
                      const current = financialTerms.enabledTaxes || [];

                      let updated;
                      if (e.checked) {
                        updated = [...current, tax._id]; // add
                      } else {
                        updated = current.filter(id => id !== tax._id); // remove
                      }

                      updateFormData.updateFinancialTerms({
                        enabledTaxes: updated
                      });
                    }}
                    className="form-check-input"
                  />
                  <label htmlFor={`tax-${tax._id}`} className="form-check-label">
                    {tax.taxType.toUpperCase()} ({tax.taxRate}%)
                  </label>
                </div>
              ))
            ) : (
              <small className="text-muted">
                No taxes configured for this facility
              </small>
            )}

            <small className="text-muted d-block mt-2">
              Select one or more taxes to apply
            </small>
          </div>
        </div>

        <div className="col-sm-6 mb-4">
          <div className="d-flex align-items-center">

            <select
              className="form-select"
              value={digitaxSelection}
              onChange={(e) => {
                const value = e.target.value;

                setDigitaxSelection(value);

                if (value === "") {
                  updateFormData.updateFinancialTerms({
                    digitaxProfileId: null,
                    digitaxEnabled: false
                  });

                } else if (value === "legacy") {
                  updateFormData.updateFinancialTerms({
                    digitaxProfileId: null,
                    digitaxEnabled: true
                  });

                } else {
                  updateFormData.updateFinancialTerms({
                    digitaxProfileId: value,
                    digitaxEnabled: true
                  });
                }
              }}
            >
              <option value="">
                Do not submit to DigiTax
              </option>

              <option value="legacy">
                Use Facility DigiTax Configuration
              </option>

              {digitaxProfiles
                .filter(profile => profile.enabled)
                .map(profile => (
                  <option
                    key={profile._id}
                    value={profile._id}
                  >
                    {profile.name}
                  </option>
                ))}
            </select>

          </div>
        </div>



        {/* Penalties */}
        <div className="col-md-6 mb-3">
          <div className="form-group">
            <label>Late Payment Penalty</label>
            <Dropdown
              value={financialTerms.selectedPenaltyId}
              options={[
                { label: "No penalty", value: null },
                ...penalties.map(penalty => ({
                  label: `${penalty.name} - ${penalty.type === 'percentage'
                    ? `${penalty.percentage}%`
                    : `${penalty.amount} ${(basicInfo.selectedCurrency && basicInfo.selectedCurrency.currencySymbol) || ''}`
                    }`,
                  value: penalty._id
                }))
              ]}
              onChange={(e) => updateFormData.updateFinancialTerms({ selectedPenaltyId: e.value })}
              placeholder="Select penalty (optional)"
              className="w-100"
              showClear
              filter
            />
            <small className="text-muted">
              Penalty applied for late rent payments
            </small>
          </div>
        </div>

        {/* Reminders */}
        <div className="col-md-6 mb-3">
          <div className="form-group">
            <label>Payment Reminders</label>
            <Dropdown
              value={financialTerms.selectedReminderId}
              options={[
                { label: "No reminders", value: null },
                ...reminders.map(reminder => ({
                  label: `${reminder.name} - ${reminder.remindOn?.afterOverdue?.days?.length
                      ? `${reminder.remindOn.afterOverdue.days.join(', ')} days after overdue`
                      : reminder.remindOn?.invoiceDate
                        ? 'on invoice date'
                        : reminder.remindOn?.dueDate
                          ? 'on due date'
                          : ''
                    }`,
                  value: reminder._id
                }))
              ]}
              onChange={(e) =>
                updateFormData.updateFinancialTerms({
                  selectedReminderId: e.value
                })
              }
              placeholder="Select reminder (optional)"
              className="w-100"
              showClear
              filter
            />
            <small className="text-muted">
              Automatic reminders before payment due date
            </small>
          </div>
        </div>

        {/* Navigation */}
        <div className="col-12 mt-4">
          <div className="d-flex justify-content-between">
            <Button
              label="Previous: Lease Terms"
              icon="ti ti-arrow-left"
              className="p-button-secondary"
              onClick={onPrevious}
            />
            <Button
              label="Next: Payment Methods"
              icon="ti ti-arrow-right"
              iconPos="right"
              className="p-button-primary"
              onClick={handleNext}
              disabled={!basicInfo.selectedCurrency || !financialTerms.monthlyRent || !financialTerms.paymentDueDate}
            />
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default FinancialTermsTab;